'use client';

import Script from 'next/script';
import { useConsent } from '@/core/components/providers/ConsentProvider';

interface DeferredTanganicaScriptsProps {
    scripts: string[];
    // CSP nonce for this request, forwarded from proxy.ts via the x-nonce
    // header — required since script-src has no 'unsafe-inline'.
    nonce?: string;
}

// Tanganica's pasted snippet (GTM + whatever tags its container fires) has no
// control over how it's loaded — merchants paste it verbatim. Rendering it
// eagerly in <head> (as raw <script> tags) puts a merchant-controlled, often
// heavy bundle (GTM + gtag + Meta Pixel + Bing UET, seen up to ~700KB) on the
// main thread during the LCP window. strategy="lazyOnload" defers execution
// to browser idle time, same as the LiveSession loader, without
// changing the script content itself (the <template> copy in layout.tsx
// still satisfies install-checkers that scan raw page source).
//
// Rendered within (store)/layout.tsx inside ConsentProvider so it respects
// cookie consent decisions the same way SiteAnalytics does.
export function DeferredTanganicaScripts({ scripts, nonce }: DeferredTanganicaScriptsProps) {
    const { consent } = useConsent();
    if (consent !== 'all' || !scripts.length) return null;

    return (
        <>
            {scripts.map((code, index) => (
                <Script
                    key={`tanganica-${index}`}
                    id={`tanganica-${index}`}
                    nonce={nonce}
                    strategy="lazyOnload"
                    dangerouslySetInnerHTML={{ __html: code }}
                />
            ))}
        </>
    );
}
